import { db } from './firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteField,
  onSnapshot,
  query,
  where,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
} from 'firebase/firestore';

const CIRCLES_COL = 'artPingPongCircles';
const INVITES_COL = 'artPingPongInvites';

export async function togglePinnedCircle(userId, circleId, isPinned) {
  const userRef = doc(db, 'users', userId);
  await updateDoc(userRef, {
    pinnedCircles: isPinned ? arrayRemove(circleId) : arrayUnion(circleId),
  });
}

function generateDisplayNameFromEmail(email) {
  if (!email) return 'Guest';
  return email.split('@')[0];
}

function generateInviteCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}

export async function createCircle(userId, name, userProfile) {
  const circleRef = doc(collection(db, CIRCLES_COL));
  const inviteCode = generateInviteCode();

  const circle = {
    name,
    createdBy: userId,
    memberIds: [userId],
    memberInfo: {
      [userId]: {
        displayName: userProfile?.displayName || generateDisplayNameFromEmail(userProfile?.email),
        photoURL: userProfile?.photoURL || null,
        role: 'admin',
      },
    },
    inviteCode,
    createdAt: serverTimestamp(),
    lastUpdated: serverTimestamp(),
    messages: [],
  };

  await setDoc(circleRef, circle);

  const inviteRef = doc(db, INVITES_COL, inviteCode);
  await setDoc(inviteRef, {
    inviteCode,
    circleId: circleRef.id,
    createdBy: userId,
    createdAt: serverTimestamp(),
    expiresAt: null,
  });

  await updateDoc(doc(db, 'users', userId), {
    circleIds: arrayUnion(circleRef.id),
  });

  return circleRef.id;
}

async function findInvite(code) {
  const upperCode = code.toUpperCase();

  try {
    const directSnap = await getDoc(doc(db, INVITES_COL, upperCode));
    if (directSnap.exists()) return directSnap.data();
  } catch (e) { /* permissions or network */ }

  try {
    const q = query(collection(db, INVITES_COL), where('inviteCode', '==', upperCode));
    const snapshot = await getDocs(q);
    if (!snapshot.empty) return snapshot.docs[0].data();
  } catch (e) { /* permissions or network */ }

  try {
    const circleQ = query(collection(db, CIRCLES_COL), where('inviteCode', '==', upperCode));
    const circleSnap = await getDocs(circleQ);
    if (!circleSnap.empty) {
      const d = circleSnap.docs[0];
      return { circleId: d.id, inviteCode: upperCode };
    }
  } catch (e) { /* permissions or network */ }

  return null;
}

export async function getCircleByInviteCode(code) {
  const invite = await findInvite(code);
  if (!invite) throw new Error('Invalid invite code');

  const { circleId, expiresAt } = invite;
  if (expiresAt && expiresAt.toDate && expiresAt.toDate() < new Date()) throw new Error('Invite code expired');

  const circleSnap = await getDoc(doc(db, CIRCLES_COL, circleId));
  if (!circleSnap.exists()) throw new Error('Circle not found');

  return { id: circleSnap.id, ...circleSnap.data() };
}

export async function joinByInviteCode(code, userId, userProfile) {
  const invite = await findInvite(code);
  if (!invite) throw new Error('Invalid invite code');

  const { circleId, expiresAt } = invite;
  if (expiresAt && expiresAt.toDate && expiresAt.toDate() < new Date()) throw new Error('Invite code expired');

  return joinCircle(circleId, userId, userProfile);
}

export async function joinCircle(circleId, userId, userProfile) {
  const circleRef = doc(db, CIRCLES_COL, circleId);
  const snap = await getDoc(circleRef);
  if (!snap.exists()) throw new Error('Circle not found');

  const data = snap.data();
  if (data.memberIds?.includes(userId)) return circleId;

  await updateDoc(circleRef, {
    memberIds: arrayUnion(userId),
    [`memberInfo.${userId}`]: {
      displayName: userProfile?.displayName || generateDisplayNameFromEmail(userProfile?.email),
      photoURL: userProfile?.photoURL || null,
      role: 'member',
    },
    lastUpdated: serverTimestamp(),
  });

  await setDoc(doc(db, 'users', userId), {
    circleIds: arrayUnion(circleId),
    displayName: userProfile?.displayName || generateDisplayNameFromEmail(userProfile?.email),
  }, { merge: true });

  return circleId;
}

export async function updateCircleName(circleId, newName) {
  await updateDoc(doc(db, CIRCLES_COL, circleId), {
    name: newName,
    lastUpdated: serverTimestamp(),
  });
}

export async function removeMember(circleId, memberId) {
  const circleRef = doc(db, CIRCLES_COL, circleId);
  await updateDoc(circleRef, {
    memberIds: arrayRemove(memberId),
    [`memberInfo.${memberId}`]: deleteField(),
    lastUpdated: serverTimestamp(),
  });
  await updateDoc(doc(db, 'users', memberId), {
    circleIds: arrayRemove(circleId),
  });
}

export async function leaveCircle(circleId, userId) {
  return removeMember(circleId, userId);
}

export function subscribeToCircle(circleId, callback) {
  return onSnapshot(doc(db, CIRCLES_COL, circleId), (snap) => {
    if (snap.exists()) {
      callback({ id: snap.id, ...snap.data() });
    }
  });
}

export async function startGame(circleId, config) {
  const circleRef = doc(db, CIRCLES_COL, circleId);
  await updateDoc(circleRef, {
    activeGame: {
      gameId: config.gameId,
      totalRounds: config.rounds || 1,
      currentRound: 1,
      currentDrawer: config.currentDrawer,
      prompt: config.prompt || null,
      emojis: config.emojis || null,
      scenePrompt: config.scenePrompt || null,
      startedBy: config.currentDrawer,
      startedAt: serverTimestamp(),
    },
    lastUpdated: serverTimestamp(),
  });
}

export async function passTurn(circleId, nextDrawerUid, newPrompt) {
  const circleRef = doc(db, CIRCLES_COL, circleId);
  const snap = await getDoc(circleRef);
  if (!snap.exists()) return;

  const game = snap.data().activeGame;
  if (!game) return;

  const nextRound = game.currentRound + 1;
  if (nextRound > game.totalRounds) {
    await updateDoc(circleRef, {
      activeGame: deleteField(),
      lastUpdated: serverTimestamp(),
    });
    return { ended: true };
  }

  await updateDoc(circleRef, {
    'activeGame.currentDrawer': nextDrawerUid,
    'activeGame.prompt': newPrompt || null,
    'activeGame.currentRound': nextRound,
    lastUpdated: serverTimestamp(),
  });
  return { ended: false, round: nextRound };
}

export async function endGame(circleId) {
  const circleRef = doc(db, CIRCLES_COL, circleId);
  await updateDoc(circleRef, {
    activeGame: deleteField(),
    lastUpdated: serverTimestamp(),
  });
}

export function subscribeToUserCircles(circleIds, callback) {
  if (!circleIds?.length) {
    callback([]);
    return () => {};
  }

  const batches = [];
  for (let i = 0; i < circleIds.length; i += 10) {
    batches.push(circleIds.slice(i, i + 10));
  }

  const batchResults = new Array(batches.length).fill(null);

  const unsubscribes = batches.map((batch, batchIndex) => {
    const q = query(collection(db, CIRCLES_COL), where('__name__', 'in', batch));
    return onSnapshot(q, (snapshot) => {
      batchResults[batchIndex] = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      callback(batchResults.filter(Boolean).flat());
    });
  });

  return () => unsubscribes.forEach((unsub) => unsub());
}
